//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { FileUploader } from '@campaigns/components/shared/file-uploader/FileUploader';
import { FormField } from '@campaigns/components/shared/product-details-modal/drawer/form/FormField';
import { MAX_IMAGE_SIZE_BYTES } from '@campaigns/constants/SamplingImageConstants';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import {
  URL_REGEX_RULE,
  isProductImageContentTypeValid,
  isProductImageUrlValid,
} from '@campaigns/constants/sampling/SamplingProductValidationRulesConstants';
import { useProductModal } from '@campaigns/contexts/ProductModalContext';
import { useImageUpload } from '@campaigns/hooks/useImageUpload';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import { Alert } from '@components/atoms/alert/Alert';
import {
  PrimaryButton,
  PrimaryLinkButton,
} from '@components/atoms/button/Button';
import { BodyText, CaptionText } from '@components/atoms/text/Text';
import { TextInput } from '@components/atoms/text-input/TextInput';
import { FormFieldController } from '@components/form/field-controller/FormFieldController';
import { DEFAULT_EMPTY_VALUE } from '@constants/DefaultEmptyValues';
import { isDefined, isTruthy } from '@utils/filterPredicates';
import { SUPPORTED_IMAGE_MIME_TYPES } from '@utils/mimeTypes';

interface ProductImageUploadProps {
  error?: string;
  isDisabled: boolean;
  isViewOnly: boolean;
  onClearImage: () => void;
  onFileSelect: (file: File | undefined) => void;
  onPreview: () => void;
  onToggleInputMode: () => void;
  updateVariantImages: (url: string) => void;
}

export const ProductImageUpload = ({
  error,
  isDisabled = false,
  isViewOnly = false,
  onClearImage,
  onFileSelect,
  onPreview,
  onToggleInputMode,
  updateVariantImages,
}: ProductImageUploadProps) => {
  const { isImageForAllVariants } = useProductModal();
  const { clearErrors, control, getValues, trigger } =
    useFormContext<EditableBVSamplingProduct>();
  const [isImageUrlInputMode, setIsImageUrlInputMode] = useState(false);

  const { error: imageUploadError, handleFilesSelected } = useImageUpload({
    onClearImage,
    onFileSelect,
  });

  const displayError = error ?? imageUploadError;

  const handlePreview = async () => {
    const isValid = await trigger('imgUrl');
    if (isValid) {
      onPreview();
    }
  };

  const handleImageUrlChange = async (value: string) => {
    clearErrors('imgUrl');
    if (!value || !isProductImageUrlValid(value)) {
      return;
    }
    const isContentTypeValid = await isProductImageContentTypeValid(value);
    // Bail if the field changed while the content-type check was pending.
    if (
      isContentTypeValid &&
      isImageForAllVariants &&
      getValues('imgUrl') === value
    ) {
      updateVariantImages(value);
    }
  };

  const toggleInputMode = () => {
    onClearImage();
    setIsImageUrlInputMode(!isImageUrlInputMode);
    onToggleInputMode();
  };

  const renderImageUrlInput = () => {
    if (isViewOnly) {
      const imgUrl = getValues('imgUrl');
      return (
        <BodyText>{isTruthy(imgUrl) ? imgUrl : DEFAULT_EMPTY_VALUE}</BodyText>
      );
    }

    return (
      <InputRow>
        <FormFieldController
          control={control}
          name='imgUrl'
          rules={{
            required: {
              value: true,
              message: 'Please enter a product image URL',
            },
            pattern: {
              value: URL_REGEX_RULE,
              message: 'Please enter a valid URL',
            },
            validate: {
              validContentType: async (value: string | undefined) => {
                if (!value) {
                  return true;
                }
                const isValid = await isProductImageContentTypeValid(value);
                return (
                  isValid || 'Invalid product image URL or unsupported format'
                );
              },
            },
          }}
        >
          {({ error, invalid, onChange, ...fieldProps }) => (
            <StyledTextInput
              aria-label='Product image URL'
              {...fieldProps}
              errorText={error?.message}
              hasError={invalid}
              onChange={(value) => {
                onChange(value);
                void handleImageUrlChange(value);
              }}
              placeholder='https://image-address.com/image.jpg'
              trackingId={`${SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_INPUT_FIELD}-imgUrl`}
            />
          )}
        </FormFieldController>
        <StyledPreviewButton
          onClick={handlePreview}
          trackingId={
            SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_PREVIEW_IMAGE_BUTTON
          }
        >
          Add
        </StyledPreviewButton>
      </InputRow>
    );
  };

  const renderUploadInput = () => (
    <>
      {/*
         FileUploader isn't a React Hook Form registered input, so while in upload mode we keep
         a hidden `imgUrl` field mounted to hold the field's validation state and error messages.
         This ensures image validation works consistently even though the user interaction happens
         via the FileUploader control.
      */}
      <HiddenFormField>
        <FormField
          inputType='textarea'
          instructions={`File types supported: 'jpeg', 'jpg', 'bmp', 'gif', 'png', and 'webp'`}
          label='Product Image'
          name='imgUrl'
          placeholder='https://image-address.com/image.jpg'
          rules={{
            required: {
              value: true,
              message: 'Please upload a product image',
            },
            validate: {
              validUrl: async (value: string) => {
                if (!value) {
                  return true;
                }
                const isValid = await isProductImageContentTypeValid(value);
                return (
                  isValid || 'Invalid product image URL or unsupported format'
                );
              },
            },
          }}
        />
      </HiddenFormField>
      {isDefined(displayError) && <Alert type='error'>{displayError}</Alert>}
      <FileUploader
        actionText='Click to select'
        existingAssetUrls={[]}
        isDisabled={isDisabled || isViewOnly}
        maxTotalSize={MAX_IMAGE_SIZE_BYTES}
        onFilesSelected={handleFilesSelected}
        shouldAcceptMultipleFiles={false}
        supportedFileTypes={SUPPORTED_IMAGE_MIME_TYPES}
        title={<BodyText>Upload image</BodyText>}
        trackingId={
          SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_INPUT_FIELD_UPLOAD_IMAGE
        }
      />
    </>
  );

  return (
    <Container>
      {isImageUrlInputMode || isViewOnly
        ? renderImageUrlInput()
        : renderUploadInput()}
      {!isViewOnly && (
        <ToggleContainer>
          <CaptionText>or</CaptionText>
          {isImageUrlInputMode ? (
            <PrimaryLinkButton
              onClick={toggleInputMode}
              trackingId={
                SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_UPLOAD_IMAGE_TOGGLE_BUTTON
              }
            >
              Upload image
            </PrimaryLinkButton>
          ) : (
            <PrimaryLinkButton
              onClick={toggleInputMode}
              trackingId={
                SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_IMAGE_URL_TOGGLE_BUTTON
              }
            >
              Enter image link instead
            </PrimaryLinkButton>
          )}
        </ToggleContainer>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[2]};
`;

const InputRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${({ theme }) => theme.echo.spacing.xxs};
`;

const StyledTextInput = styled(TextInput)`
  flex: 1;
`;

const StyledPreviewButton = styled(PrimaryButton)`
  flex-shrink: 0;
`;

const ToggleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacings[1]};
`;

const HiddenFormField = styled.div`
  display: none;
`;
